import { useRef, type ReactNode } from 'react';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useScrollEnds } from '../../hooks/useScrollEnds';
import { ease } from '../../core/format/motion';
import { btnIcon } from '../../ui/styles';
type Props<T> = {
  /** Uniek id: de pijlknoppen verwijzen ernaar. */
  id: string;
  /** Naam van de rij voor schermlezers, bijv. "Reviews". */
  label: string;
  items: readonly T[];
  itemKey: (item: T) => string;
  renderItem: (item: T, index: number) => ReactNode;
  /** Breedte per kaart, per scherm. De volgende kaart moet half in beeld steken. */
  itemClassName: string;
  /** Kaarten kort na elkaar binnen laten komen (eenmalig). */
  stagger?: boolean;
  className?: string;
};
const arrowBtn = btnIcon;

/**
 * De schuifrail van de site: één rij kaarten om doorheen te vegen, te scrollen of met de pijlen
 * te bladeren. Op elk scherm hetzelfde; alleen het aantal kaarten in beeld verschilt.
 *
 * - De volgende kaart steekt half in beeld, zodat je ziet dat er meer is.
 * - Een haarlijn toont hoe ver je bent; de pijlen gaan uit aan begin en eind.
 * - Past alles in beeld, dan geen lijn en geen pijlen.
 * - Kaarten in één rij zijn even hoog (de kaart zelf vult de hoogte met `h-full`/`mt-auto`).
 * - Met het toetsenbord: Tab naar de rij, dan de pijltjestoetsen; of Tab door de knoppen erin.
 */
export function Rail<T>({
  id,
  label,
  items,
  itemKey,
  renderItem,
  itemClassName,
  stagger = false,
  className = ''
}: Props<T>) {
  const railRef = useRef<HTMLUListElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollXProgress
  } = useScroll({
    container: railRef
  });
  const {
    atStart,
    atEnd
  } = useScrollEnds(railRef);
  const fits = atStart && atEnd;
  const nudge = (dir: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: dir * rail.clientWidth * 0.8,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  return <div className={className} data-id="element-8764" data-mp="3n7l8b4zdbgda">
      <ul id={id} ref={railRef} aria-label={label} tabIndex={0}
    // Ruimte boven en onder: een kaart die bij aanwijzen optilt (met schaduw), valt niet buiten de rail.
    className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-6 px-6 pb-6 pt-2 outline-none [scrollbar-width:none] focus-visible:ring-2 focus-visible:ring-ink/40 sm:gap-6 lg:-mx-10 lg:scroll-px-10 lg:px-10 [&::-webkit-scrollbar]:hidden" data-id="element-8765" data-mp="3f4j2o8a31vzl">
        {items.map((item, i) => <motion.li key={itemKey(item)} className={`flex shrink-0 snap-start ${itemClassName}`}
      // Kort na elkaar binnen (40 ms per kaart); alleen de eerste vier tellen mee, de rest staat buiten beeld.
      initial={stagger && !reduce ? {
        opacity: 0,
        y: 20
      } : false} whileInView={stagger && !reduce ? {
        opacity: 1,
        y: 0
      } : undefined} viewport={{
        once: true,
        margin: '0px 0px -10% 0px'
      }} transition={{
        duration: 0.3,
        delay: Math.min(i, 4) * 0.04,
        ease
      }} data-id="element-8766" data-mp="3u34knp7k2nq4" data-mpi={i}>
            {renderItem(item, i)}
          </motion.li>)}
      </ul>

      {!fits && <div className="mt-4 flex items-center gap-6" data-id="element-8767" data-mp="148potqyrab9n">
          <div aria-hidden="true" className="h-px flex-1 bg-ink/15" data-id="element-8768" data-mp="q9zirltyl195">
            <motion.div style={{
          scaleX: scrollXProgress
        }} className="h-px origin-left bg-ink" data-id="element-8769" data-mp="v9vnxoee6yqa" />
          </div>
          <div className="flex gap-2" data-id="element-8770" data-mp="gkpsmbiodsno">
            <button type="button" onClick={() => nudge(-1)} disabled={atStart} aria-label="Vorige" aria-controls={id} className={arrowBtn} data-id="element-8771" data-mp="gjoaxo75m7wz">
              <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8772" data-mp="7kt72irl19wu" />
            </button>
            <button type="button" onClick={() => nudge(1)} disabled={atEnd} aria-label="Volgende" aria-controls={id} className={arrowBtn} data-id="element-8773" data-mp="cj4haulshmfl">
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8774" data-mp="3tsazp0w5x9uv" />
            </button>
          </div>
        </div>}
    </div>;
}